import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useUnitListStore } from "../useUnitListStore";
import { Box, Skeleton, Typography } from "@mui/material";
import { SafeLeaseDataGrid } from "@safelease/components";
import { useColumnDefs } from "./columnDefs";
import { useDebounce } from "../../../utilities/useDebounce";
import { GridRowParams } from "@mui/x-data-grid-premium";
import { useHistory } from "react-router";
import { UnitDataForUnitListDashboard } from "@safelease/service-utilities";
import occupiedUnitsDetailsService from "../../../services/occupiedUnitsDetails.service";
import { mixpanelEventHandler } from "../../../utilities/reactMixpanelHandler";
import { useAuth } from "../../../auth";

type UnitsTableProps = {};

function UnitsTable({}: UnitsTableProps) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const history = useHistory();
  const auth = useAuth();

  const { selectedLocationId, selectedRelationshipId, selectedTab, searchTerm } = useUnitListStore((state) => ({
    selectedLocationId: state.selectedLocationId,
    selectedRelationshipId: state.selectedRelationshipId,
    selectedTab: state.selectedTab,
    searchTerm: state.searchTerm,
  }));

  const columnDefs = useColumnDefs();

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const pendingSelection = !selectedLocationId || !selectedRelationshipId;
  const unitDataQuery = useQuery({
    queryKey: [
      "getTcUnitsByFacilityId",
      {
        page: paginationModel.page,
        limit: paginationModel.pageSize,
        locationId: selectedLocationId,
        statusFilter: selectedTab,
        searchTerm: debouncedSearchTerm,
      },
    ],
    queryFn: async () => {
      const response = await occupiedUnitsDetailsService.getPaginatedOccupiedUnitDataForLocation(selectedLocationId.toString(), {
        page: paginationModel.page,
        limit: paginationModel.pageSize,
        statusFilter: selectedTab,
        searchTerm: debouncedSearchTerm || undefined,
      });
      return response.data;
    },
    enabled: !!selectedLocationId && !!selectedRelationshipId,
  });

  if (pendingSelection) {
    return <Typography>Select a portfolio and facility to view units</Typography>;
  }

  if (unitDataQuery.isLoading) {
    return <Skeleton variant="rounded" sx={{ width: "100%", height: "100%" }} />;
  }

  if (unitDataQuery.isError || !unitDataQuery.data) {
    return <Typography>Error loading units</Typography>;
  }

  return (
    <Box sx={{ flex: 1, overflow: "auto" }}>
      <SafeLeaseDataGrid
        columns={columnDefs}
        rows={unitDataQuery.data.returnList}
        onRowClick={(params: GridRowParams<UnitDataForUnitListDashboard>) => {
          mixpanelEventHandler("Units - Row Clicked", {
            userEmail: auth.user?.email,
            userId: auth.user?.id,
            relationshipId: selectedRelationshipId,
            locationId: selectedLocationId?.toString(),
            leaseId: params.row.leaseId,
            unitName: params.row.unitName,
            tenantName: params.row.tenantName,
          });
          history.push(`/units/lease-details/${params.row.leaseId}`);
        }}
        getRowId={(row) => row.leaseId} // need to specify unitId as the row id
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowCount={unitDataQuery.data.totalCount}
        paginationMode="server"
        pageSizeOptions={[25, 50, 75]} // Options for page size in the pagination bar
        sx={{
          // Target the rows and customize the border below each row
          ".MuiDataGrid-row": {
            cursor: "pointer",
            "&:not(:last-of-type)": {
              // Target all but the last row to add a border
              borderBottom: "2px solid rgba(235, 239, 247, 1)", // Customize the border color and thickness
            },
          },
          ".MuiDataGrid-columnHeaders": {
            backgroundColor: "#F9FAFB",
            borderBottom: "1px solid rgba(235, 239, 247, 1)", // Customize the border color and thickness for the header
            borderTop: "1px solid rgba(235, 239, 247, 1)", // Customize the border color and thickness for the header
          },
        }}
        hideFooter={false}
      />
    </Box>
  );
}

export { UnitsTable };
